import {
  booleanAttribute,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  Renderer2,
  output,
  inject,
  input,
} from '@angular/core';

import { trackByProp } from '@core/common-legacy';
import { I18nService, TranslatePipe } from '@core/i18n-legacy';
import type { Placement } from '@core/placements-types';
import type { Stage } from '@core/stages-types';
import { StageGroup } from '@core/stages-types';
import type { ConnectedOverlayDirective } from '@core/user-interface-legacy';
import {
  DropdownComponent,
  DropdownHeaderComponent,
  ScrollableDirective,
} from '@core/user-interface-legacy';

import { StageItemComponent } from './stage-item/stage-item.component';
import type { StageWithProgress } from './types';

@Component({
  selector: 'rt-candidate-stage-menu',
  styleUrls: ['./candidate-stage.menu.component.less'],
  templateUrl: './candidate-stage.menu.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    DropdownComponent,
    DropdownHeaderComponent,
    ScrollableDirective,
    StageItemComponent,
    TranslatePipe,
  ],
})
export class CandidateStageMenuComponent {
  private readonly elRef = inject(ElementRef);
  private readonly renderer = inject(Renderer2);
  private readonly i18n = inject(I18nService);

  public readonly stageGroupTranslations: Record<StageGroup, string> = {
    [StageGroup.Applicants]: this.i18n.translate('app.stages.divider.applicants_stages'),
    [StageGroup.Active]: this.i18n.translate('app.stages.divider.active_process_stages'),
    [StageGroup.Hires]: this.i18n.translate('app.stages.divider.hires_stages'),
    [StageGroup.Other]: this.i18n.translate('app.stages.divider.other_stages'),
  };

  public readonly dropdownRef = input<ConnectedOverlayDirective>(undefined!);
  public readonly placement = input<Placement>(undefined!);
  public readonly stagesWithProgress = input<StageWithProgress[]>(undefined!);
  public readonly canProceed = input(true, { transform: booleanAttribute });

  public stageClick = output<{
    placement: Placement;
    stage: Stage;
  }>();
  public closeClick = output<void>();
  public showDetailsClick = output<Partial<Stage>>();

  public trackById = trackByProp<Stage>({ prop: 'id' });

  public ngOnInit(): void {
    const { top } = this.dropdownRef().overlayRef.overlayElement.getBoundingClientRect();
    this.renderer.setStyle(
      this.elRef.nativeElement.querySelector('.stages-dropdown'),
      'maxHeight',
      `calc(95vh - ${top}px)`,
    );
  }

  protected onStageClick(placement: Placement, stage: StageWithProgress): void {
    if (stage.progress === 'current') {
      return;
    }

    this.canProceed() && this.stageClick.emit({ placement, stage });
  }
}
